import React, { useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import Tooltip from '@mui/material/Tooltip';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Stack, SxProps, Typography } from '@mui/material';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { generatePoiDisplayName } from './utils';
import { PoiStatus, PointOfInterest } from '@/__generated__/graphql';
import { usePoiTemplateFields } from '@/components/Analysis/ReviewScreen/hooks/usePoiTemplateFields';
import { Navigation } from '@/components/shared/Navigation/Navigation';
import { ReviewAction } from '@/components/Analysis/ReviewScreen/PointsOfInterest/SummaryCard/ReviewAction';
import { NavigationItem } from '@/types';
import { abyssColors } from '@/theme/colors';
import { useFeatureFlag } from '@/hooks';
import { LocationTag } from '@/constants';

type Props = { sx?: SxProps; selectedPoiInfo?: PointOfInterest };

export const PoiNavigation = ({ selectedPoiInfo }: Props) => {
  const [selectedPoiId, setSelectedPoiId] = useAtom(poiState.selectedPointOfInterestId);
  const poisInComponent = useAtomValue(poiState.poiFilteredByCheckbox);
  const poisData =
    poisInComponent?.map((poi) => ({
      id: poi.id,
      name: poi.name ?? '',
    })) ?? [];

  const isMiEngineerOrApiInspector = useFeatureFlag(['api-inspector', 'mi-engineer']);

  const handleChange = (poiData: Partial<NavigationItem>) => {
    setSelectedPoiId(poiData.id);
  };
  const { templateOfSelectedPoi } = usePoiTemplateFields({
    data: selectedPoiInfo!,
  });

  let statusIcon;
  let displayName;

  if (selectedPoiInfo) {
    const { name, poiId, type, status, isVerified } = selectedPoiInfo;

    displayName = generatePoiDisplayName({ name, poiId, poiType: type });

    const iconProps = { sx: { ml: 1 } };

    if (status && [PoiStatus.Completed, PoiStatus.Remediated].includes(status)) {
      statusIcon = (
        <Tooltip title="Completed/Remediated">
          <CheckCircleIcon {...iconProps} sx={{ ...iconProps.sx, color: 'success.main' }} />
        </Tooltip>
      );
    } else if (isVerified) {
      statusIcon = (
        <Tooltip title="Verified">
          <CheckCircleIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0B9DEC' }} />
        </Tooltip>
      );
    }
  }

  const hasReviewActionField = templateOfSelectedPoi?.fields?.some(
    ({ key }) => key === 'review_action'
  );

  const reviewContent = useMemo(() => {
    if (!selectedPoiInfo || !hasReviewActionField || !isMiEngineerOrApiInspector) return;

    return (
      <ReviewAction
        pointOfInterest={selectedPoiInfo}
        templateOfSelectedPoi={templateOfSelectedPoi}
        location={LocationTag.ViewerSelectedPoi}
      />
    );
  }, [selectedPoiInfo, hasReviewActionField, isMiEngineerOrApiInspector, templateOfSelectedPoi]);

  return (
    <Navigation
      items={poisData}
      toolTip={displayName}
      selectedItemId={selectedPoiId}
      handleChange={handleChange}
      content={reviewContent}
      location={LocationTag.ViewerSelectedPoi}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ maxWidth: reviewContent ? '11rem' : '22rem' }}
      >
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 200,
            color: abyssColors.primary[700],
            fontWeight: '700',
          }}
        >
          {displayName}
        </Typography>
        {statusIcon}
      </Stack>
    </Navigation>
  );
};
